import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import {
  User as UserIcon, Home, Users, CreditCard, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Package as PackageIcon,
} from "lucide-react";
import {
  ROOM_TYPES, PAYMENT_METHODS, UNIFORM_SIZES, RELATIONS, BANK_ACCOUNTS,
} from "@/data/registrationConstants";
import { EQUIPMENT_PACKAGES, formatIDR } from "@/data/equipmentPackages";

const STEPS = [
  { key: "data_diri", label: "Data Diri",     icon: UserIcon },
  { key: "paket",     label: "Paket & Kamar", icon: PackageIcon },
  { key: "mahram",    label: "Data Mahram",   icon: Users },
  { key: "bayar",     label: "Metode Bayar",  icon: CreditCard },
];

const isNIK = (v) => /^[0-9]{16}$/.test(v || "");
const isPassport = (v) => /^[A-Z0-9]{6,12}$/i.test((v || "").trim());
const isPhone = (v) => /^(\+62|62|0)[0-9]{8,14}$/.test((v || "").replace(/\s|-/g, ""));

// -------------------- Step components --------------------
function DataDiriStep({ f, set, err }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Nama lengkap (sesuai paspor) *" testid="wiz-name" err={err.name}>
        <input value={f.name} onChange={(e) => set("name", e.target.value.toUpperCase())} className="wiz-input" placeholder="MUHAMMAD RIZKI" />
      </Field>
      <Field label="Email *" testid="wiz-email" err={err.email}>
        <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="wiz-input" />
      </Field>
      <Field label="No. HP / WhatsApp *" testid="wiz-phone" err={err.phone}>
        <input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="wiz-input" placeholder="0812xxxxxxxx" />
      </Field>
      <Field label="NIK (16 digit KTP) *" testid="wiz-nik" err={err.nik}>
        <input inputMode="numeric" maxLength={16} value={f.nik} onChange={(e) => set("nik", e.target.value.replace(/\D/g, ""))} className="wiz-input" placeholder="3275xxxxxxxxxxxx" />
      </Field>
      <Field label="Tanggal Lahir *" testid="wiz-dob" err={err.dob}>
        <input type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} className="wiz-input" />
      </Field>
      <Field label="Jenis Kelamin *" testid="wiz-gender" err={err.gender}>
        <select value={f.gender} onChange={(e) => set("gender", e.target.value)} className="wiz-input">
          <option value="">— Pilih —</option>
          <option value="L">Laki-laki</option>
          <option value="P">Perempuan</option>
        </select>
      </Field>
      <Field label="No. Paspor" testid="wiz-passport" err={err.passport_no}>
        <input value={f.passport_no} onChange={(e) => set("passport_no", e.target.value.toUpperCase())} className="wiz-input" placeholder="B1234567 (opsional)" />
      </Field>
      <Field label="Masa berlaku paspor" testid="wiz-passport-exp">
        <input type="date" value={f.passport_expiry} onChange={(e) => set("passport_expiry", e.target.value)} className="wiz-input" />
      </Field>
      <Field label="Alamat lengkap *" testid="wiz-addr" err={err.address} full>
        <input value={f.address} onChange={(e) => set("address", e.target.value)} className="wiz-input" placeholder="Jl. Contoh No. 123 RT 01 RW 02" />
      </Field>
      <Field label="Kota *" testid="wiz-city" err={err.city}>
        <input value={f.city} onChange={(e) => set("city", e.target.value)} className="wiz-input" />
      </Field>
      <Field label="Provinsi *" testid="wiz-province" err={err.province}>
        <input value={f.province} onChange={(e) => set("province", e.target.value)} className="wiz-input" />
      </Field>
      <Field label="Kode Pos" testid="wiz-postal">
        <input inputMode="numeric" maxLength={5} value={f.postal_code} onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, ""))} className="wiz-input" />
      </Field>
      <Field label="Ukuran Seragam" testid="wiz-uniform">
        <select value={f.uniform_size} onChange={(e) => set("uniform_size", e.target.value)} className="wiz-input">
          <option value="">— Pilih —</option>
          {UNIFORM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
    </div>
  );
}

function PaketStep({ f, set, pkg }) {
  const base = pkg?.price_idr || 0;
  const selEq = EQUIPMENT_PACKAGES.find((e) => e.id === f.equipment_id);
  const roomAdd = ROOM_TYPES.find((r) => r.key === f.room_type)?.addIdr || 0;
  const eqTotal = selEq ? selEq.price_idr * (parseInt(f.pax, 10) || 1) : 0;
  const roomTotal = (base + roomAdd) * (parseInt(f.pax, 10) || 1);
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <p className="overline text-primary">Paket dipilih</p>
        <p className="font-serif text-xl text-primary mt-1">{pkg?.name_id || "—"}</p>
        <p className="text-sm text-muted-foreground">{pkg?.duration} · {pkg?.departure || "—"} · {pkg?.airline || ""}</p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Tipe Kamar *</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {ROOM_TYPES.map((r) => {
            const active = f.room_type === r.key;
            return (
              <button
                key={r.key}
                type="button"
                data-testid={`wiz-room-${r.key}`}
                onClick={() => set("room_type", r.key)}
                className={`text-left p-4 rounded-lg border transition-all ${active ? "border-accent bg-accent/10 ring-2 ring-accent" : "border-border hover:border-accent/50"}`}
              >
                <p className="font-serif text-primary">{r.label_id}</p>
                <p className="text-xs mt-1 text-muted-foreground">
                  {r.addIdr === 0 ? "Harga dasar" : `+ ${formatIDR(r.addIdr)} / pax`}
                </p>
                <p className="mt-2 font-serif text-lg text-primary">{formatIDR(base + r.addIdr)}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Jumlah Jamaah *" testid="wiz-pax">
          <input type="number" min="1" max="20" value={f.pax} onChange={(e) => set("pax", Math.max(1, parseInt(e.target.value, 10) || 1))} className="wiz-input" />
        </Field>
        <Field label="Preferensi Keberangkatan" testid="wiz-departure-pref">
          <input value={f.departure_pref || ""} onChange={(e) => set("departure_pref", e.target.value)} className="wiz-input" placeholder={pkg?.departure || "—"} />
        </Field>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Paket Perlengkapan (Opsional)</p>
        <select
          value={f.equipment_id || ""}
          onChange={(e) => {
            const eq = EQUIPMENT_PACKAGES.find((x) => x.id === e.target.value);
            set("equipment_id", eq?.id || "");
            set("equipment_name", eq?.name_id || "");
            set("equipment_price_idr", eq?.price_idr || 0);
          }}
          className="wiz-input"
          data-testid="wiz-equipment"
        >
          <option value="">— Tidak ambil perlengkapan —</option>
          {EQUIPMENT_PACKAGES.map((eq) => (
            <option key={eq.id} value={eq.id}>{eq.name_id} · {formatIDR(eq.price_idr)}</option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-4" data-testid="wiz-price-summary">
        <p className="overline text-primary mb-2">Ringkasan Estimasi</p>
        <div className="text-sm space-y-1">
          <div className="flex justify-between"><span>Paket × {f.pax} pax</span><span>{formatIDR(roomTotal)}</span></div>
          {selEq && <div className="flex justify-between"><span>Perlengkapan × {f.pax}</span><span>{formatIDR(eqTotal)}</span></div>}
          <div className="flex justify-between font-serif text-primary text-lg pt-2 mt-2 border-t border-border">
            <span>Total Estimasi</span><span>{formatIDR(roomTotal + eqTotal)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MahramStep({ f, set, err }) {
  const dobAge = f.dob ? Math.floor((Date.now() - new Date(f.dob).getTime()) / (365.25 * 86400000)) : null;
  const mahramRequired = f.gender === "P" && dobAge !== null && dobAge < 45;
  return (
    <div className="space-y-4">
      {mahramRequired ? (
        <div className="rounded-md bg-amber-50 border-l-3 border-amber-500 p-3 text-xs text-amber-900">
          <strong>Wajib mahram:</strong> Sesuai regulasi umrah untuk jamaah wanita &lt; 45 tahun, data mahram diperlukan.
        </div>
      ) : (
        <div className="rounded-md bg-sky-50 border-l-3 border-sky-500 p-3 text-xs text-sky-900">
          Data mahram <strong>opsional</strong> untuk profil Anda. Isi jika berangkat bersama pasangan / mahram.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label={`Nama Mahram${mahramRequired ? " *" : ""}`} testid="wiz-mahram-name" err={err.mahram_name}>
          <input value={f.mahram_name} onChange={(e) => set("mahram_name", e.target.value.toUpperCase())} className="wiz-input" placeholder="ABDULLAH BIN AHMAD" />
        </Field>
        <Field label={`Hubungan${mahramRequired ? " *" : ""}`} testid="wiz-mahram-rel" err={err.mahram_relation}>
          <select value={f.mahram_relation} onChange={(e) => set("mahram_relation", e.target.value)} className="wiz-input">
            <option value="">— Pilih —</option>
            {RELATIONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </Field>
        <Field label="No. HP Mahram" testid="wiz-mahram-phone">
          <input value={f.mahram_phone} onChange={(e) => set("mahram_phone", e.target.value)} className="wiz-input" placeholder="0812xxxxxxxx" />
        </Field>
        <Field label="No. Paspor Mahram" testid="wiz-mahram-passport">
          <input value={f.mahram_passport_no} onChange={(e) => set("mahram_passport_no", e.target.value.toUpperCase())} className="wiz-input" placeholder="B1234567" />
        </Field>
      </div>
    </div>
  );
}

function BayarStep({ f, set, err }) {
  const [refState, setRefState] = React.useState({ status: "idle" });
  const validateRef = React.useCallback(async (code) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) { setRefState({ status: "idle" }); return; }
    setRefState({ status: "checking" });
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/referral/validate/${encodeURIComponent(c)}`);
      const data = await r.json();
      if (data.valid) setRefState({ status: "valid", ...data });
      else setRefState({ status: "invalid" });
    } catch {
      setRefState({ status: "invalid" });
    }
  }, []);
  React.useEffect(() => {
    // If URL has ?ref= prefill and lock the field
    if (!f.referral_code) {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      if (urlRef) { set("referral_code", urlRef.toUpperCase()); }
    }
    // eslint-disable-next-line
  }, []);
  React.useEffect(() => {
    const t = setTimeout(() => validateRef(f.referral_code), 400);
    return () => clearTimeout(t);
  }, [f.referral_code, validateRef]);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Pilih Metode Pembayaran *</p>
        <div className="space-y-3">
          {PAYMENT_METHODS.map((m) => {
            const active = f.payment_method === m.key;
            return (
              <button
                key={m.key}
                type="button"
                data-testid={`wiz-pay-${m.key}`}
                onClick={() => set("payment_method", m.key)}
                className={`w-full text-left p-4 rounded-lg border transition-all ${active ? "border-accent bg-accent/10 ring-2 ring-accent" : "border-border hover:border-accent/50"}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-4 w-4 rounded-full mt-1 shrink-0 ${active ? "bg-accent" : "border border-border"}`} />
                  <div>
                    <p className="font-serif text-primary">{m.label_id}</p>
                    <p className="text-xs text-muted-foreground mt-1">{m.desc_id}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {err.payment_method && <p className="text-xs text-red-600 mt-2">{err.payment_method}</p>}
      </div>

      <div className="rounded-lg border border-border p-4">
        <p className="overline text-primary mb-3">Rekening Transfer (Bayar setelah pendaftaran)</p>
        <div className="space-y-2 text-sm">
          {BANK_ACCOUNTS.map((b) => (
            <div key={b.number} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2 bg-muted/40 rounded">
              <div>
                <p className="font-semibold text-primary">{b.bank}</p>
                <p className="text-xs text-muted-foreground">a.n. {b.name}</p>
              </div>
              <span className="font-mono text-primary text-base">{b.number}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Setelah pendaftaran, unggah <strong>bukti transfer</strong> pada portal jamaah untuk diverifikasi admin.
        </p>
      </div>

      <div className="rounded-lg border border-accent/40 bg-accent/5 p-4" data-testid="referral-block">
        <p className="overline !text-accent mb-1">Kode Referral (Opsional)</p>
        <p className="text-xs text-muted-foreground mb-3">
          Dapatkan potongan <strong className="text-accent">Rp 500.000</strong> jika dapat kode dari kerabat yang sudah pernah umrah dengan Batik Travel.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={f.referral_code || ""}
            onChange={(e) => set("referral_code", e.target.value.toUpperCase())}
            data-testid="wiz-referral"
            placeholder="Contoh: FARO9B1E"
            className="flex-1 px-3 py-2 border border-border rounded-md text-sm font-mono uppercase bg-white"
            maxLength={20}
          />
        </div>
        {refState.status === "checking" && <p className="text-xs text-muted-foreground mt-2">Memeriksa…</p>}
        {refState.status === "valid" && (
          <p className="text-xs text-green-700 mt-2" data-testid="ref-valid">
            ✓ Kode valid dari <strong>{refState.owner_name}</strong>. Anda dapat potongan Rp {refState.discount_amount.toLocaleString("id-ID")} setelah booking dikonfirmasi.
          </p>
        )}
        {refState.status === "invalid" && f.referral_code && (
          <p className="text-xs text-red-600 mt-2" data-testid="ref-invalid">Kode referral tidak ditemukan.</p>
        )}
      </div>

      <div className="rounded-lg border border-border p-4">
        <label className="flex gap-3 items-start cursor-pointer" data-testid="wiz-agree">
          <input type="checkbox" checked={f.agree} onChange={(e) => set("agree", e.target.checked)} className="mt-1 h-4 w-4" />
          <span className="text-sm text-foreground/80">
            Saya menyetujui <a href="/tos" target="_blank" rel="noopener noreferrer" className="text-accent underline">syarat &amp; ketentuan</a> serta <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-accent underline">kebijakan privasi</a> BATIK Tour &amp; Travel. Data yang saya isi adalah benar.
          </span>
        </label>
        {err.agree && <p className="text-xs text-red-600 mt-2">{err.agree}</p>}
      </div>
    </div>
  );
}

// -------------------- Helpers --------------------
function Field({ label, children, testid, err, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </label>
  );
}

// -------------------- Main --------------------
export default function RegisterJamaah() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, isAuthed } = useAuth();
  const [pkg, setPkg] = React.useState(null);
  const [pkgLoading, setPkgLoading] = React.useState(true);
  const [step, setStep] = React.useState(0);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState({});

  const [f, setF] = React.useState({
    name: "", email: "", phone: "",
    nik: "", dob: "", gender: "",
    passport_no: "", passport_expiry: "",
    address: "", city: "", province: "", postal_code: "",
    uniform_size: "",
    room_type: "quad", pax: 1, departure_pref: "",
    equipment_id: "", equipment_name: "", equipment_price_idr: 0,
    mahram_name: "", mahram_relation: "", mahram_phone: "", mahram_passport_no: "",
    payment_method: "",
    agree: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Prefill from auth
  React.useEffect(() => {
    if (user) {
      setF((p) => ({
        ...p,
        name: p.name || user.name || "",
        email: p.email || user.email || "",
        phone: p.phone || user.phone || "",
      }));
    }
  }, [user]);

  React.useEffect(() => {
    setPkgLoading(true);
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => setPkg(null)).finally(() => setPkgLoading(false));
  }, [slug]);

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!f.name.trim()) e.name = "Nama wajib diisi";
      if (!/^[^@]+@[^@]+\.[^@]+$/.test(f.email)) e.email = "Email tidak valid";
      if (!isPhone(f.phone)) e.phone = "No. HP tidak valid";
      if (!isNIK(f.nik)) e.nik = "NIK harus 16 digit";
      if (!f.dob) e.dob = "Tanggal lahir wajib diisi";
      if (!f.gender) e.gender = "Pilih jenis kelamin";
      if (f.passport_no && !isPassport(f.passport_no)) e.passport_no = "Format paspor tidak valid";
      if (!f.address.trim()) e.address = "Alamat wajib diisi";
      if (!f.city.trim()) e.city = "Kota wajib diisi";
      if (!f.province.trim()) e.province = "Provinsi wajib diisi";
    }
    if (step === 2) {
      const age = f.dob ? Math.floor((Date.now() - new Date(f.dob).getTime()) / (365.25 * 86400000)) : null;
      const mahramRequired = f.gender === "P" && age !== null && age < 45;
      if (mahramRequired) {
        if (!f.mahram_name.trim()) e.mahram_name = "Wajib untuk jamaah wanita < 45 tahun";
        if (!f.mahram_relation) e.mahram_relation = "Wajib pilih hubungan";
      }
    }
    if (step === 3) {
      if (!f.payment_method) e.payment_method = "Pilih metode pembayaran";
      if (!f.agree) e.agree = "Setujui syarat & ketentuan terlebih dahulu";
    }
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate()) {
      toast.error("Mohon lengkapi data yang wajib");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        package_id: pkg.id,
        package_name: pkg.name_id,
        name: f.name, email: f.email, phone: f.phone,
        pax: parseInt(f.pax, 10) || 1,
        departure_pref: f.departure_pref || null,
        nik: f.nik, dob: f.dob, gender: f.gender,
        passport_no: f.passport_no || null,
        passport_expiry: f.passport_expiry || null,
        address: f.address, city: f.city, province: f.province, postal_code: f.postal_code || null,
        uniform_size: f.uniform_size || null,
        room_type: f.room_type,
        mahram_name: f.mahram_name || null,
        mahram_relation: f.mahram_relation || null,
        mahram_phone: f.mahram_phone || null,
        mahram_passport_no: f.mahram_passport_no || null,
        payment_method: f.payment_method,
        registration_step: "complete",
      };
      if (f.equipment_id) {
        payload.equipment_id = f.equipment_id;
        payload.equipment_name = f.equipment_name;
        payload.equipment_price_idr = f.equipment_price_idr;
      }
      // Referral code from URL param (?ref=CODE) or manual input, whichever is set
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const refCode = (f.referral_code || urlRef || "").trim();
      if (refCode) payload.referral_code = refCode;
      const r = await api.post("/bookings", payload);
      toast.success(`Pendaftaran berhasil — ${r.data.booking_number}`);
      // Redirect to portal for next steps (upload docs + payment proof)
      nav("/my-bookings?welcome=1", { replace: true });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Gagal mendaftar. Silakan coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (pkgLoading) return <div className="pt-32 pb-32 container-x text-muted-foreground">Loading…</div>;
  if (!pkg) return (
    <div className="pt-32 pb-32 container-x">
      <p className="text-muted-foreground">Paket tidak ditemukan.</p>
      <Link to="/packages" className="btn-outline mt-6">Kembali ke Paket</Link>
    </div>
  );

  const StepIcon = STEPS[step].icon;

  return (
    <div className="pt-32 lg:pt-40 pb-32 min-h-screen bg-muted/20" data-testid="register-jamaah-page">
      <Helmet><title>{`Pendaftaran Jamaah — ${pkg.name_id}`}</title></Helmet>

      <style>{`
        .wiz-input {
          width: 100%; height: 42px; padding: 0 12px;
          border: 1px solid var(--border); background: white; color: var(--foreground);
          border-radius: 8px; font-size: 14px; outline: none; transition: box-shadow .15s, border-color .15s;
        }
        .wiz-input:focus { border-color: var(--accent); box-shadow: 0 0 0 2px rgba(183,138,46,.25); }
        textarea.wiz-input { height: auto; min-height: 80px; padding-top: 8px; }
      `}</style>

      <div className="container-x max-w-4xl">
        <Link to={`/packages/${slug}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm mb-6" data-testid="back-to-detail">
          <ArrowLeft className="h-4 w-4" /> Kembali ke detail paket
        </Link>

        <header className="mb-8">
          <p className="overline">Pendaftaran Jamaah</p>
          <h1 className="font-serif text-3xl lg:text-4xl text-primary mt-2">{pkg.name_id}</h1>
          <p className="text-sm text-muted-foreground mt-1">{pkg.duration} · {pkg.departure} · {pkg.airline}{pkg.airport ? ` · ${pkg.airport}` : ""}</p>
          {!isAuthed && (
            <div className="mt-4 rounded-md bg-sky-50 border-l-3 border-sky-500 p-3 text-xs text-sky-900">
              Login untuk pengalaman terbaik: <Link to="/login" className="underline">Masuk</Link> · <Link to="/register" className="underline">Daftar Akun</Link>. Anda tetap bisa mendaftar sebagai tamu.
            </div>
          )}
        </header>

        {/* Stepper */}
        <div className="mb-8 flex items-center gap-2 flex-wrap" data-testid="wiz-stepper">
          {STEPS.map((s, i) => {
            const active = i === step, done = i < step;
            const Icon = s.icon;
            return (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold ${
                  active ? "bg-primary text-white" : done ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"
                }`} data-testid={`wiz-step-${s.key}`}>
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{i + 1}. {s.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-px w-3 sm:w-8 ${done ? "bg-emerald-400" : "bg-border"}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="bg-card border border-border rounded-xl p-6 lg:p-8">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
            <div className="h-9 w-9 rounded-full bg-accent/20 grid place-items-center text-primary">
              <StepIcon className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Langkah {step + 1} dari {STEPS.length}</p>
              <p className="font-serif text-lg text-primary">{STEPS[step].label}</p>
            </div>
          </div>

          {step === 0 && <DataDiriStep f={f} set={set} err={err} />}
          {step === 1 && <PaketStep f={f} set={set} pkg={pkg} />}
          {step === 2 && <MahramStep f={f} set={set} err={err} />}
          {step === 3 && <BayarStep f={f} set={set} err={err} />}

          <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={step === 0}
              data-testid="wiz-prev"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-muted disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" /> Sebelumnya
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="wiz-next"
                className="btn-primary inline-flex items-center gap-2"
              >
                Lanjut <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting || !f.agree || !f.payment_method}
                data-testid="wiz-submit"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {submitting ? "Mendaftar..." : "Selesaikan Pendaftaran"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
